import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { memo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validatePublishApp } from 'pages/listApp/helpers/validate';
import usePublishApp from 'pages/listApp/hooks/usePublishApp';
import { PLATFORM_ENUM } from '@tenant/utility/constants/enum';
import AppVersion from './AppVersion';
import AppVersionLog from './AppVersionLog';
import useRePublishApp from 'pages/listApp/hooks/useRePublishApp';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

const PublishApp = ({
  data,
  children,
  isRepublish = false,
  onClose = () => {},
}) => {
  const { appId } = useParams();
  const [open, setOpen] = useState(false);
  const { mutate } = usePublishApp();
  const { mutate: mutateRePublish, isPending: isPendingRePublish } =
    useRePublishApp();
  const action = isRepublish ? 'Re-Publish' : 'Publish';

  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleSubmit = async (values) => {
    if (isRepublish) {
      mutateRePublish(
        { ...values, AppId: appId },
        {
          onSuccess() {
            toast.success('Re-Publish app successfully.');
            handleClose();
          },
        },
      );
      return;
    }

    mutate(
      { AppId: appId, ...values },
      {
        onSuccess() {
          toast.success('Publish app successfully.');
          handleClose();
        },
      },
    );
  };

  return (
    <>
      <Box component='span' onClick={handleOpen}>
        {children}
      </Box>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ fontSize: 20 }}>
          Are you sure you want to {action.toLowerCase()} this app?{' '}
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{
              AppVersion: data?.AppVersion,
              Platform: data?.Platform,
              LaunchedType: data?.LaunchedType,
            }}
            validationSchema={!isRepublish ? validatePublishApp : undefined}
            onSubmit={handleSubmit}
          >
            {({ values, errors, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <Stack direction='column' spacing={4}>
                  {data.Platform === PLATFORM_ENUM['Web'] && (
                    <>
                      <AppVersion
                        title={'Web App Version'}
                        fieldKey={'AppVersion'}
                        errorText={errors.AppVersion}
                        valueText={values.AppVersion}
                        setFieldValue={setFieldValue}
                      />

                      <AppVersionLog
                        title={'Web App Version Log'}
                        fieldKey={'AppVersionLog'}
                        errorText={errors.AppVersionLog}
                        valueText={values.AppVersionLog}
                        setFieldValue={setFieldValue}
                      />
                    </>
                  )}

                  {data.Platform === PLATFORM_ENUM['Mobile'] && (
                    <>
                      <AppVersion
                        title={'Mobile App Version'}
                        fieldKey={'MobileAppVersion'}
                        errorText={errors.MobileAppVersion}
                        valueText={values.MobileAppVersion}
                        setFieldValue={setFieldValue}
                      />

                      <AppVersionLog
                        title={'Mobile App Version Log'}
                        fieldKey={'MobileAppVersionLog'}
                        errorText={errors.MobileAppVersionLog}
                        valueText={values.MobileAppVersionLog}
                        setFieldValue={setFieldValue}
                      />
                    </>
                  )}

                  {data.Platform === PLATFORM_ENUM['Hybrid'] && (
                    <>
                      <AppVersion
                        title={'Web App Version'}
                        fieldKey={'AppVersion'}
                        errorText={errors.AppVersion}
                        valueText={values.AppVersion}
                        setFieldValue={setFieldValue}
                      />
                      <AppVersionLog
                        title={'Web App Version Log'}
                        fieldKey={'AppVersionLog'}
                        errorText={errors.AppVersionLog}
                        valueText={values.AppVersionLog}
                        setFieldValue={setFieldValue}
                      />

                      <AppVersion
                        title={'Mobile App Version'}
                        fieldKey={'MobileAppVersion'}
                        errorText={errors.MobileAppVersion}
                        valueText={values.MobileAppVersion}
                        setFieldValue={setFieldValue}
                      />
                      <AppVersionLog
                        title={'Mobile App Version Log'}
                        fieldKey={'MobileAppVersionLog'}
                        errorText={errors.MobileAppVersionLog}
                        valueText={values.MobileAppVersionLog}
                        setFieldValue={setFieldValue}
                      />
                    </>
                  )}
                </Stack>
                <Stack
                  spacing={2}
                  direction='row'
                  sx={{ mt: 5 }}
                  justifyContent='flex-end'
                >
                  <Button
                    variant='contained'
                    color='secondary'
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={isPendingRePublish}
                    variant='contained'
                    color='primary'
                    type='submit'
                  >
                    {action}
                  </LoadingButton>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(PublishApp);

PublishApp.propTypes = {
  data: PropTypes.object,
  children: PropTypes.node,
  isRepublish: PropTypes.bool,
  onClose: PropTypes.func,
};
